import { Component, OnInit } from "@angular/core";
import { IProgressBarColourRules } from "@directives/_misc/v-progress-bar/v-progress-bar.component";
import { IAggregateFilterEvent, IFilterBarTab } from "@directives/_resourceFilters/filter-bar/filter-bar.models";
import { DateRange } from "@enums/DateRange.enum";
import { IGeofence } from "@interfaces/IGeofences";
import { IJourney } from "@interfaces/IJourney";
import { IVehicle } from "@interfaces/IVehicle";
import { DateService, DateUnit } from "@services/date.service";
import { GeofenceService, IGeofenceVisit } from "@services/geofence.service";
import { GroupService } from "@services/group.service.ajs";
import { JourneyService } from "@services/journey.service";
import { TranslationService } from "@services/translation.service";
import { IVehicleUtilisationRecord, UtilisationService } from "@services/utilisation.service";
import { VehicleService } from "@services/vehicle.service";
import { VeriDate } from "@services/veriDate.service";
import { IGroupWithIds } from "../../resourceManagement/groups/_models/IGroupWithIds";
import "./vehicle-utilisation.component.scss";

export interface ITimeLabel {
    label: string;
    subLabel: string;
}

enum UtilisationSource {
    Journey,
    Depot
}

@Component({
    selector: "vehicle-utilisation",
    template: require("./vehicle-utilisation.component.html")
})
export class VehicleUtilisationComponent implements OnInit {

    loading = false;

    defaultDateRange = DateRange.Latest7Days;
    colourRules: IProgressBarColourRules = {
        GreenZoneStart: 75,
        YellowZoneStart: 25
    };

    timeLabels: ITimeLabel[] = [];

    filterEvent: IAggregateFilterEvent<IVehicleUtilisationRecord>;
    filterTabs: IFilterBarTab<IVehicleUtilisationRecord>[] = [];

    displayVehicles: IVehicleUtilisationRecord[] = [];
    allVehicles: IVehicleUtilisationRecord[] = [];

    utilisationSource = UtilisationSource.Journey;

    // ---

    vehicles: IVehicle[];
    groups: IGroupWithIds[];

    geofences: IGeofence[];
    geofenceVisits: IGeofenceVisit[];
    journeys: IJourney[];

    constructor(
        private readonly groupService: GroupService,
        private readonly geofenceService: GeofenceService,
        private readonly journeyService: JourneyService,
        private readonly vehicleService: VehicleService,
        private readonly veriDate: VeriDate,
        private readonly utilisationService: UtilisationService,
        private readonly translationService: TranslationService,
        private readonly dateService: DateService) {
    }

    ngOnInit(): void {
        this.createTabs();
    }

    filterBySearchText(v: IVehicleUtilisationRecord, text: string): boolean {
        if (v.vehicle.ShortReg.toLocaleLowerCase().indexOf(text) >= 0 || v.vehicle.Reg.toLocaleLowerCase().indexOf(text) >= 0) {
            return true;
        }

        return false;
    }

    onFilterChanged(event: IAggregateFilterEvent<IVehicleUtilisationRecord>): void {

        // We sometimes get weird events come through, need to understand why, until then this fixes a bug
        if (!event.trigger) {
            return;
        }

        this.filterEvent = event;

        if (this.filterEvent.trigger == "date") {
            this.updateTimeLabels();
            this.updateData(true);
            return;
        }

        this.displayVehicles = this.filterEvent.filteredData;
    }

    onUtilisationTypeChange(source: UtilisationSource): void {
        this.utilisationSource = source;
        this.updateData(true);
    }

    // -------------------

    private updateData(invalidateCache = false): void {
        this.loading = true;
        this.allVehicles = [];

        const baseDataPromise = this.getBaseData();
        const utilisationDataPromise = this.utilisationSource == UtilisationSource.Journey
            ? this.getJourneyUtilisationData(invalidateCache)
            : this.getDepotUtilisationData(invalidateCache);

        var promises: any = [baseDataPromise, utilisationDataPromise];

        Promise.all(promises)
            .then(() => {
                this.createUtilisationRecords();
            })
            .finally(() => {
                this.loading = false;
            });
    }

    private getBaseData(): Promise<any> {
        const groupsPromise = this.groupService.getGroupsWithFullMappings().then(groups => { this.groups = groups }) as Promise<any>;
        const vehiclesPromise = this.vehicleService.getVehicles({ includePeripheralMappings: true }).then(vehicles => { this.vehicles = vehicles; }) as Promise<any>;

        return Promise.all([groupsPromise, vehiclesPromise]);
    }

    private getDepotUtilisationData(invalidateCache: boolean): Promise<any> {
        const geofencesPromise = this.geofenceService.getGeofences({ invalidateCache }).then(geofences => { this.geofences = geofences; }) as Promise<any>;
        const geofenceVisitsPromise = this.geofenceService
            .getGeofenceVisits(this.veriDate.createMoment(this.filterEvent.date[0]), this.veriDate.createMoment(this.filterEvent.date[1]))
            .then(geofenceVisits => { this.geofenceVisits = geofenceVisits; }) as Promise<any>;

        return Promise.all([geofencesPromise, geofenceVisitsPromise]);
    }

    private getJourneyUtilisationData(invalidateCache: boolean): Promise<IJourney[]> {
        return this.journeyService
            .getAllJourneys(this.filterEvent.date[0], this.filterEvent.date[1], false, false, invalidateCache)
            .then(journeys => {
                this.journeys = journeys;
            }) as Promise<IJourney[]>;
    }

    private createUtilisationRecords(): void {
        if (this.utilisationSource == UtilisationSource.Journey) {
            this.allVehicles = this.utilisationService.createUtilisationRecordsForJourneys(
                this.filterEvent.date[0],
                this.filterEvent.date[1],
                this.vehicles,
                this.journeys);
            return;
        }

        this.allVehicles = this.utilisationService.createUtilisationRecordsForDepots(
            this.filterEvent.date[0],
            this.filterEvent.date[1],
            this.groups,
            this.vehicles,
            this.geofenceVisits,
            this.geofences);
    }

    private updateTimeLabels(): void {
        this.timeLabels = [];

        if (!this.filterEvent?.date) {
            return;
        }

        const minutesInRange = Math.abs(this.dateService.getDiff(this.filterEvent.date[0], this.filterEvent.date[1], DateUnit.Min));

        const hoursInRange = Math.ceil(minutesInRange / 60);

        let maxLabels = 12;
        let hoursPerLabel = hoursInRange / maxLabels;
        const dayMode = hoursInRange / 24 > 4;

        // If we span multiple days, then switch to day mode, if not hour mode
        if (dayMode) {
            while (hoursPerLabel % 24 != 0 && maxLabels > 0) {
                maxLabels--;
                hoursPerLabel = hoursInRange / maxLabels;
            }
        }
        else {
            while (hoursPerLabel % 1 != 0 && maxLabels > 0) {
                maxLabels--;
                hoursPerLabel = hoursInRange / maxLabels;
            }
        }

        for (let i = 0; i < maxLabels; i++) {

            const hour = i * hoursPerLabel;
            const dateTime = this.dateService.addOrSubtractTime(this.filterEvent.date[0], hour, DateUnit.Hour);

            this.timeLabels.push({
                label: this.dateService.formatDate(dateTime, { stringDayOnly: dayMode, timeOnly: !dayMode }),
                subLabel: this.dateService.formatDate(dateTime)
            });
        }
    }

    private createTabs(): void {
        this.filterTabs = [];
        
        this.filterTabs.push({ title: this.translationService.t("FleetUtilisation.All"), predicate: (vu) => true });

        this.filterTabs.push({ title: this.translationService.t("FleetUtilisation.MediumUtilisation"), predicate: (vu: IVehicleUtilisationRecord) => vu.utilisationPercentage >= 25 && vu.utilisationPercentage <75 });
        this.filterTabs.push({ title: this.translationService.t("FleetUtilisation.LowUtilisation"), predicate: (vu: IVehicleUtilisationRecord) => vu.utilisationPercentage < 25 && vu.utilisationPercentage > 0 });
        this.filterTabs.push({ title: this.translationService.t("FleetUtilisation.Unused"), predicate: (vu: IVehicleUtilisationRecord) => vu.utilisationPercentage == 0 });
    }
}
